import { useSnowplowContext } from "@simplybusiness/services";
import { useAtomCallback } from "jotai/utils";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInterventions } from "@simplybusiness/mobius-interventions";
import {
  useAddressModes,
  useAnswer,
  useAnswersForQuestions,
  useError,
  useGroups,
  useIsEditing,
  useQuestionnaireActions,
  useQuestionnaireNavigation,
  useSectionById,
  useSectionIndexById,
  useValidationDependencies,
  useValidations,
  useVisibilityMap,
  type Answers,
  type UpdateAnswerPayload,
} from "../../../../../features/questionnaire";
import { questionnaireReducerAtom } from "../../../../../features/questionnaire/state/questionnaireReducerAtom";
import { computeMergedAnswersWithGroups } from "../../../../../features/questionnaire/utils/computeMergedAnswersWithGroups";
import { useDependentQuestions } from "../../../../../hooks/useDependentQuestions";
import { useLocale } from "../../../../../hooks/useLocale";
import { useSendAnswers } from "../../../../../hooks/useSendAnswers";
import { nextButtonRefAtom } from "../../../Actions/state/nextButtonRefAtom";
import { isObject, isOneOf, snakeCase } from "../../../../../utils";
import { createValidator } from "../../../validation/form";
import type { ValidationResponse } from "../../../validation/question/rules/types";
import { getQuestionMap, TRIGGERS, type QuestionMap } from "./questions";
import {
  shouldTriggerValidationEvent,
  getGroupName,
} from "./shouldTriggerValidationEvent";
import type { AnswerChangeEvent, QuestionFactoryProps } from "./types";
import type { TradeSelectionMetaWithMethod } from "./questions/TradeQuestion/AmbiguousTradeModal/useAmbiguousTradeInterceptor";

const CUSTOM_VALIDATION_TYPES = ["email", "tel", "zipcode"] as const;

// Stable empty array to avoid recreating on every render
const EMPTY_DEPENDENCIES: string[] = [];

export const QuestionFactory = (props: QuestionFactoryProps) => {
  const {
    display,
    options,
    shouldUpdate,
    trackAs,
    associatedQuestionName,
    questionFieldProps,
    suggested,
    sectionId,
  } = props;
  const { type, config, component: displayType } = display;
  const { updateAnswer, removeError, setError, setAddressMode } =
    useQuestionnaireActions();
  const validations = useValidations();
  const validateQuestion = useMemo(
    () => createValidator(validations ?? []),
    [validations],
  );
  const addressModes = useAddressModes();
  const isEditing = useIsEditing();
  const groups = useGroups();
  const visibility = useVisibilityMap();
  const { locale } = useLocale();
  const { snowplow } = useSnowplowContext();
  const section = useSectionById(sectionId);
  const sectionIndex = useSectionIndexById(sectionId);
  const { getInterventions } = useInterventions();
  const { sendAnswers } = useSendAnswers();
  const getQuestionnaireState = useAtomCallback(
    useCallback(get => get(questionnaireReducerAtom), []),
  );
  const currentQuestionAnswer = useAnswer(questionFieldProps.name);
  const lastTrackedPrimaryDetail = useRef<string | undefined>(
    trackAs === "primary_detail_selected"
      ? currentQuestionAnswer?.toString()
      : undefined,
  );
  const { sendAnswers: sendAssumptions } = useDependentQuestions();
  const error = useError(questionFieldProps.name) ?? "";
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { handleNextClick } = useQuestionnaireNavigation();
  const nextButtonRef = useAtomValue(nextButtonRefAtom);

  // Get the pre-computed validation dependencies map (computed once via atom)
  const validationDependenciesMap = useValidationDependencies();

  // Look up dependencies for this specific question (uses stable empty array as fallback)
  const validationDependencies =
    validationDependenciesMap[questionFieldProps.name] ?? EMPTY_DEPENDENCIES;

  // Subscribe only to the answers of questions this validation depends on
  const dependentAnswers = useAnswersForQuestions(validationDependencies);

  const validate = useCallback(
    (event: AnswerChangeEvent): boolean => {
      const payload = event.target as UpdateAnswerPayload;
      const state = getQuestionnaireState();
      const mergedAnswers: Answers = computeMergedAnswersWithGroups({
        answers: state.answers,
        groups,
        payload,
      });

      // Extending validation for email, tel, and zipcode question types
      const validation = {
        validation: questionFieldProps.validation,
        ...(isOneOf(type, CUSTOM_VALIDATION_TYPES) && {
          validation: { type },
        }),
      };

      const result: ValidationResponse = validateQuestion({
        question: {
          ...questionFieldProps,
          ...validation,
          visible: true,
          display,
        },
        answers: mergedAnswers,
        visibilityMap: visibility,
        locale,
        groups,
        serverVisibility: state.serverVisibility,
      });

      setIsDirty(true);

      if (result?.valid) {
        if (error) {
          removeError(questionFieldProps.name);
        }
      } else {
        const errorMsg = String(result?.message ?? "");
        const isNewError = errorMsg !== error;
        if (isNewError) {
          setError(questionFieldProps.name, errorMsg);
          // Only trigger validation event when error is first displayed, not on subsequent validations
          if (
            shouldTriggerValidationEvent(
              display,
              questionFieldProps.name,
              groups,
            )
          ) {
            const coverName = getGroupName(questionFieldProps.name, groups);
            snowplow?.trigger("validationShown", {
              cover: coverName,
              question: questionFieldProps.name,
            });
          }
        }
      }

      return result.valid;
    },
    [
      getQuestionnaireState,
      groups,
      questionFieldProps,
      type,
      display,
      validateQuestion,
      visibility,
      error,
      removeError,
      setError,
      locale,
      snowplow,
    ],
  );

  const shouldRevalidate = (
    validationDependencies: string[],
    isDirty: boolean,
    error: string,
  ): boolean => {
    // Re-validate if this question has validation dependencies and either:
    // - has been modified (isDirty), or
    // - has an existing validation error (to clear errors after form-level validation)
    return validationDependencies.length > 0 && (isDirty || !!error);
  };

  // Re-validate question when dependent questions' answers change
  useEffect(() => {
    if (shouldRevalidate(validationDependencies, isDirty, error)) {
      validate({
        target: {
          name: questionFieldProps.name,
          value: currentQuestionAnswer,
        },
      } as AnswerChangeEvent);
    }
  }, [
    validationDependencies,
    questionFieldProps.name,
    currentQuestionAnswer,
    validate,
    isDirty,
    error,
    dependentAnswers, // Triggers re-validation when dependent answers change
  ]);

  const questionMap = getQuestionMap();
  const componentType = (displayType ?? type) as keyof QuestionMap;
  const questionConfig = questionMap[componentType] ?? questionMap.default;
  const Component = questionConfig.component;
  const trigger = questionConfig.trigger;

  if (!questionMap[componentType]) {
    console.warn(
      `No component found to render as: ${componentType}, falling back to default (TextQuestion)`,
    );
  }

  const handleActions = (event: AnswerChangeEvent) => {
    const payload = event.target as UpdateAnswerPayload & {
      shouldUpdate?: boolean; // MultiSelectGroup may pass this flag to trigger sendAnswers
    };

    // Check if blur was caused by clicking Next button
    // FocusEvent has relatedTarget, other event types don't
    const relatedTarget =
      "relatedTarget" in event ? event.relatedTarget : undefined;
    const isNextButtonClick = relatedTarget && relatedTarget === nextButtonRef;

    const isValid = validate(event);

    // Update state and send events
    updateAnswer(event);
    trackQuestionAnswer(event);
    // Since state has changed we should tell IDM to re-evaluate interventions
    void getInterventions();

    // MultiSelectGroup options with shouldUpdate flag must trigger sendAnswers regardless of validation
    // to ensure dependent questions are rendered correctly. Without this flag, standard validation applies.
    if (!isValid && !payload.shouldUpdate) {
      return;
    }

    if (shouldUpdate || payload.shouldUpdate) {
      // Update state, taking account of group answers
      const state = getQuestionnaireState();
      const mergedAnswers = computeMergedAnswersWithGroups({
        answers: state.answers,
        groups,
        payload,
      });

      // Send answers to the backend using the fresh computed answers
      // Error handling is done in useSendAnswers onError callback (sets error state, logs, notifies Airbrake)
      const sendPromise = isEditing
        ? sendAssumptions(mergedAnswers, questionFieldProps.name)
        : sendAnswers(mergedAnswers);

      // If Next button was clicked, auto-trigger navigation after blur completes
      if (isNextButtonClick) {
        sendPromise
          .then(() => {
            handleNextClick(); // Auto-trigger Next navigation
          })
          .catch(() => {
            // Blur request failed, don't navigate
          });
      } else {
        // The .catch() here prevents unhandled promise rejection warnings
        sendPromise.catch(() => {});
      }
    }
  };

  const handleBlurChanges = (event: AnswerChangeEvent) => {
    updateAnswer(event);
  };

  const trackQuestionAnswer = (event: AnswerChangeEvent) => {
    const payload = event.target as EventTarget & {
      value: string;
      name: string;
      tradeSelectionMeta?: TradeSelectionMetaWithMethod;
    };
    const { name, tradeSelectionMeta } = payload;
    let { value } = payload;

    if (isObject(value)) {
      value = String(value[name]);
    }

    if (trackAs === "form_question_answered" && !!value) {
      snowplow?.trigger("questionAnswered", {
        section: section?.title ?? section?.id ?? "",
        question: snakeCase(questionFieldProps.name),
        answer: value,
      });
    }
    if (trackAs === "primary_detail_selected" && !!value) {
      // Only fire if the value has changed from last tracked value
      if (value === lastTrackedPrimaryDetail.current) {
        return;
      }
      lastTrackedPrimaryDetail.current = value;

      const params: Record<string, unknown> = { answer: value };

      // Only include trade selection metadata when available (trade questions)
      if (tradeSelectionMeta) {
        params.searchId = tradeSelectionMeta.searchId;
        params.selectedListPosition = tradeSelectionMeta.index;
        // Prefer explicit override (e.g. general_contractor_popup) over variant-derived fallback.
        params.selectionMethod =
          tradeSelectionMeta.selectionMethod ??
          (tradeSelectionMeta.variant === "ai" ? "genAI" : "legacy");
      }

      snowplow?.trigger("primaryDetailSelected", params);
    }
  };

  const enhancedQuestionProps = {
    ...questionFieldProps,
    ...(trigger === TRIGGERS.CHANGE && { onChange: handleActions }),
    ...(trigger === TRIGGERS.BLUR && {
      onBlur: handleActions,
      onChange: handleBlurChanges,
    }),
    isInvalid: !!error,
    errorMessage: error,
    // Form inputs are NOT disabled during requests - users can continue answering
    // The Next button is disabled via useShouldBlockSubmission instead
  };

  const preFilledSource = getQuestionnaireState().config?.preFilledSource as
    | string
    | undefined;

  return (
    <Component
      options={options}
      type={type}
      config={config}
      questionFieldProps={enhancedQuestionProps}
      sectionTitle={section?.title}
      sectionIndex={sectionIndex}
      addressModes={addressModes}
      setAddressMode={setAddressMode}
      associatedQuestionName={associatedQuestionName}
      suggested={suggested}
      preFilledSource={preFilledSource}
    />
  );
};
